import React, { useCallback, useEffect, useMemo, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TodayIcon from "@mui/icons-material/Today";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { Box, Button, Chip, CircularProgress, IconButton, Paper, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import DictionaryApiService from "../../admin/system/integrate/DictionaryApiService";
import { getLocalizedStoryFromTemplate } from "../../admin/pzl_common/getEnStoryFromTemplate";
import WordOfDayMeaning from "./WordOfDayMeaning";
import WordOfDayStorySection from "./WordOfDayStorySection";
import { SUPPORT_FRENCH } from "../../config/supportFrench";
import {
    CONTENT_LANG_CHANGED_EVENT,
    CONTENT_LANG_KEY,
    getContentLanguage,
    setContentLanguage,
} from "../../services/LangPrefs";
import HomeService from "../../services/HomeService";
import Logger from "../../services/Logger";
import { trackEvent } from "../../services/GoogleAnalytics";
import { addMontrealCalendarDays, montrealYmdFromDate } from "../components/montrealCalendar";
import { usePagesTranslation } from "../locales";
import { usePageTitle } from "../puzzles/common/hooks/usePageTitle";
import SlidingPlayMistBackground from "../puzzles/sliding/SlidingPlayMistBackground";
import { SITE_BG, SURFACE } from "../../dark-theme/palette";
import { playPillButtonSx } from "../../dark-theme/surfaces";

const PANEL_PAPER_SX = {
    bgcolor: SURFACE.panel,
    backdropFilter: "blur(10px)",
    borderColor: SURFACE.border,
    boxShadow: "0 8px 28px rgba(2, 6, 18, 0.45)",
};

const dateNavIconSx = {
    ...playPillButtonSx,
    width: 44,
    height: 44,
    padding: 0,
    borderRadius: "50%",
};

const playCircleGreenSxBase = {
    padding: 0,
    borderRadius: "50%",
    color: "#fff",
    flexShrink: 0,
    background: "linear-gradient(145deg, #1b5e20 0%, #2e7d32 45%, #43a047 100%)",
    boxShadow: "0 4px 18px rgba(27, 94, 32, 0.45)",
    "&:hover": {
        background: "linear-gradient(145deg, #145214 0%, #1b5e20 50%, #2e7d32 100%)",
        boxShadow: "0 6px 22px rgba(27, 94, 32, 0.55)",
    },
    "&.Mui-disabled": {
        color: "rgba(255,255,255,0.65)",
    },
};

/** Secondary rows: play control with top margin */
const playCircleGreenSx = {
    ...playCircleGreenSxBase,
    mt: 2,
    alignSelf: "flex-start",
    width: 52,
    height: 52,
};

/** Puzzle title row: normal tap target (not oversized) */
const playCircleGreenHeaderSx = {
    ...playCircleGreenSxBase,
    width: 44,
    height: 44,
};

/** Large accent (unused on WOD puzzle row; kept for reuse) */
const playCircleGreenFeaturedSx = {
    ...playCircleGreenSxBase,
    width: 78,
    height: 78,
};

/** Play routes use puzzle instance ids only (not template ids on crossTpl / gridTpl). */
function crossPlayId(entry) {
    return entry?.crossTpl?.crossword?.id || null;
}

function gridPlayId(entry) {
    return entry?.gridTpl?.grid?.id || null;
}

function playPathForEntry(entry) {
    const crossId = crossPlayId(entry);
    if (crossId) {
        return `/puzzle/play-cross/${crossId}`;
    }
    const gid = gridPlayId(entry);
    if (gid) {
        const typ = String(entry.gridTpl?.type || "WORD_SEARCH").toUpperCase();
        return typ === "HIDDEN_MESSAGE"
            ? `/puzzle/play-hidden-message/${gid}`
            : `/puzzle/play-word-search/${gid}`;
    }
    return null;
}

/** @returns {'crossword'|'hidden'|'wordsearch'|null} */
function playKind(entry) {
    if (crossPlayId(entry)) return "crossword";
    if (gridPlayId(entry)) {
        const typ = String(entry.gridTpl?.type || "").toUpperCase();
        if (typ === "HIDDEN_MESSAGE") return "hidden";
        return "wordsearch";
    }
    return null;
}

function playLabelKey(kind) {
    if (kind === "crossword") return "wordOfDay.play-crossword";
    if (kind === "hidden") return "wordOfDay.play-hidden-message";
    if (kind === "wordsearch") return "wordOfDay.play-word-search";
    return null;
}

function apiContentLang() {
    const raw = getContentLanguage();
    return raw === "french" ? "french" : "english";
}

/** BCP 47 locale for heading date from ui.lang (see locales SUPPORTED_LANGS). */
const WORD_OF_DAY_DATE_LOCALE = {
    english: "en-CA",
    french: "fr-CA",
    chinese: "zh-CN",
    hindi: "hi-IN",
    spanish: "es",
    arabic: "ar",
    portuguese: "pt-BR",
    german: "de-DE",
    japanese: "ja-JP",
    korean: "ko-KR",
};

/** Long date in the active interface language (Montreal calendar day as local calendar date). */
function formatWordOfDayHeadingDate(ymd, interfaceLang) {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "";
    const [y, m, d] = ymd.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    const locale = WORD_OF_DAY_DATE_LOCALE[interfaceLang] || WORD_OF_DAY_DATE_LOCALE.english;
    try {
        return new Intl.DateTimeFormat(locale, { year: "numeric", month: "long", day: "numeric" }).format(date);
    } catch {
        return new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "long", day: "numeric" }).format(date);
    }
}

function getPuzzleTitle(entry) {
    if (!entry) return "";
    const title = entry.crossTpl?.title || entry.gridTpl?.title;
    if (title) return title;
    return entry.crossTpl?.name || entry.gridTpl?.name || "";
}

function WordPronunciationButton({ word, language, t }) {
    const [pronunciationLoading, setPronunciationLoading] = useState(false);
    const w = String(word || "").trim();
    if (!w) return null;
    const lang = language === "french" ? "french" : "english";
    const play = async () => {
        setPronunciationLoading(true);
        try {
            const url = await DictionaryApiService.playWordAudio(w, lang);
            if (!url) return;
            const audio = new Audio(url);
            await audio.play();
        } catch (e) {
            Logger.warn("[WordOfDay] pronunciation play failed:", e);
        } finally {
            setPronunciationLoading(false);
        }
    };
    const label = t("wordOfDay.play-pronunciation");
    return (
        <Tooltip title={label}>
            <span>
                <IconButton
                    size="medium"
                    onClick={play}
                    disabled={pronunciationLoading}
                    aria-label={label}
                    sx={{ color: "primary.main" }}
                >
                    {pronunciationLoading ? (
                        <CircularProgress size={22} sx={{ color: "primary.main" }} />
                    ) : (
                        <VolumeUpIcon />
                    )}
                </IconButton>
            </span>
        </Tooltip>
    );
}

function WodDateNav({ t, loading, selectedDate, todayYmd, showTodayBadge, onDateInput, shiftDay }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2, textAlign: "center" }}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Tooltip title={t("wordOfDay.prev-day")}>
                    <IconButton
                        aria-label={t("wordOfDay.prev-day")}
                        onClick={() => shiftDay(-1)}
                        disabled={loading}
                        sx={dateNavIconSx}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                </Tooltip>
                <TextField
                    type="date"
                    size="small"
                    label={t("wordOfDay.pick-date")}
                    value={selectedDate}
                    onChange={onDateInput}
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: todayYmd }}
                    sx={{ minWidth: 200 }}
                />
                <Tooltip title={t("wordOfDay.next-day")}>
                    <span>
                        <IconButton
                            aria-label={t("wordOfDay.next-day")}
                            onClick={() => shiftDay(1)}
                            disabled={loading || selectedDate >= todayYmd}
                            sx={dateNavIconSx}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                {showTodayBadge ? (
                    <Chip label={t("wordOfDay.today-badge")} size="small" color="success" variant="outlined" />
                ) : null}
            </Stack>
        </Paper>
    );
}

/**
 * @param {{ entry: object, t: function, size?: "default" | "header" | "large" }} props
 * header: normal-sized circle beside puzzle title; default: card rows; large: accent */
function PlayPuzzleButton({ entry, t, size = "default" }) {
    const playPath = playPathForEntry(entry);
    const kind = playKind(entry);
    const labelKey = playLabelKey(kind);
    if (playPath && labelKey) {
        const label = t(labelKey);
        const iconSize = size === "large" ? 42 : size === "header" ? 24 : 28;
        const sx = size === "large" ? playCircleGreenFeaturedSx : size === "header" ? playCircleGreenHeaderSx : playCircleGreenSx;
        return (
            <Tooltip title={label}>
                <IconButton
                    component={RouterLink}
                    to={playPath}
                    aria-label={label}
                    sx={sx}
                >
                    <PlayArrowIcon sx={{ fontSize: iconSize }} />
                </IconButton>
            </Tooltip>
        );
    }
    return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: size === "header" ? 0 : 2 }}>
            {t("wordOfDay.no-puzzle-link")}
        </Typography>
    );
}

const langToggleBtnSx = {
    minWidth: 0,
    px: 1.25,
    py: 0.5,
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: 0.04,
    lineHeight: 1.35,
    textTransform: "none",
    borderWidth: 2,
    "&.MuiButton-outlined": {
        borderWidth: 2,
    },
};

function isWordOfDayYmd(value) {
    return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function wordOfDayPath(ymd, todayYmd) {
    if (!isWordOfDayYmd(ymd) || ymd === todayYmd) return "/word-of-day";
    return `/word-of-day/${ymd}`;
}

const WordOfDayMain = () => {
    const { t, lang: interfaceLang } = usePagesTranslation();
    const { ymd: ymdParam } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [apiLang, setApiLang] = useState(() => apiContentLang());
    const todayYmd = montrealYmdFromDate();
    const dateFromRoute = isWordOfDayYmd(ymdParam)
        ? (ymdParam > todayYmd ? todayYmd : ymdParam)
        : todayYmd;
    const [selectedDate, setSelectedDate] = useState(dateFromRoute);
    const [loading, setLoading] = useState(true);
    const [result, setResult] = useState(null);

    const rows = !loading && result?.kind === "ok" && Array.isArray(result.rows) ? result.rows : [];
    const first = rows[0];

    const showTodayBadge =
        result?.kind === "ok" && typeof result.isTodayFromApi === "boolean"
            ? result.isTodayFromApi
            : selectedDate === todayYmd;

    usePageTitle(
        selectedDate && selectedDate !== todayYmd
            ? `${t("wordOfDay.title")} (${selectedDate}) | Puzzle Interact`
            : t("wordOfDay.meta-title"),
    );

    useEffect(() => {
        setSelectedDate((prev) => (prev === dateFromRoute ? prev : dateFromRoute));
    }, [dateFromRoute]);

    useEffect(() => {
        if (ymdParam && !isWordOfDayYmd(ymdParam)) {
            navigate("/word-of-day", { replace: true });
        }
    }, [ymdParam, navigate]);

    const goToDate = useCallback((ymd) => {
        const today = montrealYmdFromDate();
        const next = !isWordOfDayYmd(ymd) || ymd > today ? today : ymd;
        const path = wordOfDayPath(next, today);
        const current = (location.pathname || "").replace(/\/+$/, "") || "/";
        if (current !== path) {
            navigate(path, { replace: true });
        } else {
            setSelectedDate((prev) => (prev === next ? prev : next));
        }
    }, [location.pathname, navigate]);

    useEffect(() => {
        trackEvent("entered", {
            name: "WordOfDayMain",
            page_type: "WORD_OF_DAY",
            content_lang: apiLang,
            date: selectedDate,
        });
    }, [selectedDate, apiLang]);

    useEffect(() => {
        const sync = () => setApiLang(apiContentLang());
        window.addEventListener(CONTENT_LANG_CHANGED_EVENT, sync);
        const onStorage = (e) => {
            if (e.key === CONTENT_LANG_KEY) sync();
        };
        window.addEventListener("storage", onStorage);
        return () => {
            window.removeEventListener(CONTENT_LANG_CHANGED_EVENT, sync);
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setResult(null);
        try {
            const res = await HomeService.getWordOfTheDay({
                language: apiLang,
                date: selectedDate,
            });
            setResult(res);
            if (res.kind === "ok" && isWordOfDayYmd(res.responseDate) && res.responseDate !== selectedDate) {
                goToDate(res.responseDate);
            }
        } catch (e) {
            Logger.error("Word of day load error:", e);
            setResult({ kind: "error" });
        } finally {
            setLoading(false);
        }
    }, [apiLang, selectedDate, goToDate]);

    useEffect(() => {
        load();
    }, [load]);

    const onDateInput = (e) => {
        const v = e.target.value;
        if (!v) return;
        goToDate(v);
    };

    const shiftDay = (delta) => {
        goToDate(addMontrealCalendarDays(selectedDate, delta));
    };

    const dateNavProps = {
        t,
        loading,
        selectedDate,
        todayYmd,
        showTodayBadge,
        onDateInput,
        shiftDay,
    };

    const showGlobalDateNav = !loading && !(result?.kind === "ok" && rows.length > 0);

    const headingDateLabel = formatWordOfDayHeadingDate(selectedDate, interfaceLang);

    /** Story from the linked crossword/grid template (same localization as play pages). */
    const firstStoryPresentation = useMemo(() => {
        const tpl = first?.crossTpl || first?.gridTpl;
        if (!tpl) return null;
        const { title, text, imageUrl } = getLocalizedStoryFromTemplate(tpl);
        const has =
            String(title || "").trim() ||
            String(text || "").trim() ||
            String(imageUrl || "").trim();
        if (!has) return null;
        const story = tpl?.story;
        const storyId = story?.id ? String(story.id).trim() : null;
        const showSlidingPuzzle = Boolean(storyId && story?.sliding?.state === "PUBLISHED");
        const showJigsawPuzzle = Boolean(storyId && story?.jigsaw?.state === "PUBLISHED");
        return { title, text, imageUrl, storyId, showSlidingPuzzle, showJigsawPuzzle };
    }, [first]);

    const firstPuzzleTitle = useMemo(() => (first ? getPuzzleTitle(first) : ""), [first]);
    const firstPlayPath = useMemo(() => (first ? playPathForEntry(first) : null), [first]);
    const showPuzzleHeaderRow = Boolean(first && (firstPuzzleTitle || firstPlayPath));

    const englishOptionLabel = t("wordOfDay.content-lang-english");
    const frenchOptionLabel = t("wordOfDay.content-lang-french");

    return (
        <Box
            sx={{
                position: "relative",
                isolation: "isolate",
                width: "100%",
                flex: 1,
                minHeight: "100%",
                display: "flex",
                flexDirection: "column",
                overflowX: "hidden",
                overflowY: "auto",
                bgcolor: SITE_BG,
            }}
        >
            <SlidingPlayMistBackground variant="night" />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flex: 1,
                    width: "100%",
                    px: { xs: 2, sm: 3 },
                    py: 3,
                    boxSizing: "border-box",
                }}
            >
            <Box sx={{ mb: 3 }}>
                <Paper
                    elevation={4}
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 2,
                        p: { xs: 2.5, sm: 3 },
                        border: "none",
                        background: "linear-gradient(125deg, #0d3d1a 0%, #1b5e20 32%, #2e7d32 62%, #388e3c 100%)",
                        boxShadow: "0 8px 32px rgba(27, 94, 32, 0.28)",
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            opacity: 0.35,
                            backgroundImage: `repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0px, transparent 1px, transparent 20px, rgba(255,255,255,0.06) 21px),
                            repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0px, transparent 1px, transparent 20px, rgba(255,255,255,0.06) 21px)`,
                            pointerEvents: "none",
                        },
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            background: "radial-gradient(ellipse 120% 80% at 0% 100%, rgba(255,255,255,0.08) 0%, transparent 50%)",
                            pointerEvents: "none",
                        },
                    }}
                >
                    <TodayIcon
                        sx={{
                            position: "absolute",
                            right: { xs: 4, sm: 12 },
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: { xs: 72, sm: 100 },
                            color: "rgba(255,255,255,0.12)",
                            pointerEvents: "none",
                            zIndex: 0,
                        }}
                        aria-hidden
                    />
                    <Box sx={{ position: "relative", zIndex: 1, pr: { xs: 0, sm: 10 } }}>
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{
                                fontWeight: 800,
                                color: "#fff",
                                letterSpacing: "-0.02em",
                                textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                                lineHeight: 1.2,
                            }}
                        >
                            {t("wordOfDay.title")}
                        </Typography>
                        {headingDateLabel ? (
                            <Typography
                                variant="h6"
                                component="p"
                                sx={{
                                    mt: 1.25,
                                    fontWeight: 500,
                                    color: "rgba(255,255,255,0.9)",
                                    lineHeight: 1.4,
                                    textShadow: "0 1px 1px rgba(0,0,0,0.12)",
                                }}
                            >
                                {headingDateLabel}
                            </Typography>
                        ) : null}
                    </Box>
                </Paper>
                {SUPPORT_FRENCH ? (
                <Stack
                    direction="row"
                    spacing={0}
                    justifyContent="flex-end"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{
                        mt: 1.5,
                        gap: 0,
                        "& .MuiButton-root:first-of-type": {
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                        },
                        "& .MuiButton-root:last-of-type": {
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                        },
                    }}
                >
                    <Button
                        size="small"
                        color="success"
                        variant={apiLang === "english" ? "contained" : "outlined"}
                        onClick={() => setContentLanguage("english")}
                        aria-pressed={apiLang === "english"}
                        aria-label={englishOptionLabel}
                        sx={langToggleBtnSx}
                    >
                        {englishOptionLabel}
                    </Button>
                    <Button
                        size="small"
                        color="success"
                        variant={apiLang === "french" ? "contained" : "outlined"}
                        onClick={() => setContentLanguage("french")}
                        aria-pressed={apiLang === "french"}
                        aria-label={frenchOptionLabel}
                        sx={langToggleBtnSx}
                    >
                        {frenchOptionLabel}
                    </Button>
                </Stack>
                ) : null}
            </Box>

            {loading ? <Typography color="text.secondary">{t("wordOfDay.loading")}</Typography> : null}

            {!loading && result?.kind === "nodata" ? (
                <Typography color="text.secondary">{t("wordOfDay.no-data")}</Typography>
            ) : null}

            {!loading && result?.kind === "ok" && result.rows?.length === 0 ? (
                <Typography color="text.secondary">{t("wordOfDay.no-data")}</Typography>
            ) : null}

            {!loading && first ? (
                <Paper elevation={2} variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2, ...PANEL_PAPER_SX }}>
                    <Box
                        sx={{
                            mb: 2.5,
                            p: 2,
                            borderRadius: 2,
                            background: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "linear-gradient(135deg, rgba(129, 199, 132, 0.12) 0%, rgba(46, 125, 50, 0.08) 100%)"
                                    : "linear-gradient(135deg, rgba(232, 245, 233, 0.95) 0%, rgba(200, 230, 201, 0.35) 100%)",
                            border: "1px solid",
                            borderColor: (theme) =>
                                theme.palette.mode === "dark" ? "rgba(129, 199, 132, 0.25)" : "rgba(46, 125, 50, 0.22)",
                            borderLeftWidth: 4,
                            borderLeftColor: "#2e7d32",
                        }}
                    >
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" useFlexGap>
                            <Typography
                                variant="h4"
                                component="h2"
                                sx={{
                                    fontWeight: 800,
                                    m: 0,
                                    letterSpacing: "-0.03em",
                                    color: (theme) => (theme.palette.mode === "dark" ? "success.light" : "#1b5e20"),
                                }}
                            >
                                {first.word || ""}
                            </Typography>
                            {first.word ? (
                                <WordPronunciationButton
                                    word={first.word}
                                    language={first.language === "french" ? "french" : "english"}
                                    t={t}
                                />
                            ) : null}
                        </Stack>
                    </Box>
                    {first.word ? (
                        <Box sx={{ mb: 2 }}>
                            <WordOfDayMeaning
                                selectedWord={first.word}
                                language={first.language === "french" ? "french" : "english"}
                            />
                        </Box>
                    ) : null}
                    {showPuzzleHeaderRow ? (
                        <>
                            <Box
                                sx={{
                                    mb: 1.5,
                                    p: 1.75,
                                    borderRadius: 2,
                                    background: (theme) =>
                                        `linear-gradient(135deg, ${theme.palette.mode === "dark" ? "rgba(129, 199, 132, 0.12)" : "rgba(46, 125, 50, 0.09)"} 0%, ${theme.palette.mode === "dark" ? "rgba(76, 175, 80, 0.06)" : "rgba(67, 160, 71, 0.06)"} 100%)`,
                                    border: "1px solid",
                                    borderColor: (theme) =>
                                        theme.palette.mode === "dark" ? "rgba(129, 199, 132, 0.35)" : "rgba(46, 125, 50, 0.28)",
                                    borderLeftWidth: 4,
                                    borderLeftColor: "success.main",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        lineHeight: 1.65,
                                        color: "text.primary",
                                        fontWeight: 500,
                                        letterSpacing: 0.01,
                                    }}
                                >
                                    {t("wordOfDay.puzzle-invite")}
                                </Typography>
                            </Box>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={2}
                                sx={{ mb: firstStoryPresentation ? 1.5 : 0 }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    component="h3"
                                    sx={{ fontWeight: 600, flex: 1, minWidth: 0, color: "text.primary" }}
                                >
                                    {firstPuzzleTitle || t("wordOfDay.puzzle-default-title")}
                                </Typography>
                                {firstPlayPath ? <PlayPuzzleButton entry={first} t={t} size="header" /> : null}
                            </Stack>
                        </>
                    ) : null}
                    {firstStoryPresentation ? (
                        <WordOfDayStorySection
                            presentation={firstStoryPresentation}
                            entry={first}
                        />
                    ) : null}
                    <WodDateNav {...dateNavProps} />
                </Paper>
            ) : null}

            {showGlobalDateNav ? (
                <Box sx={{ mt: 2 }}>
                    <WodDateNav {...dateNavProps} />
                </Box>
            ) : null}

            {!loading && result?.kind === "raw" ? (
                <Typography variant="body2" color="warning.main">
                    {t("wordOfDay.unexpected-response")}
                </Typography>
            ) : null}
            </Box>
        </Box>
    );
};

export default WordOfDayMain;
